import { useCallback, useEffect, useMemo, useState } from 'react'
import Select, { SingleValue } from 'react-select'
import { Modal } from 'react-bootstrap'
import { useDashboardData, useFilters } from './live/useDashboardData'
import { DashboardParams, FilterSite, MeterType } from './live/types'
import {
  DEFAULT_SECTIONS,
  SectionConfig,
  SECTION_LABEL,
  SectionId,
  loadSections,
  saveSections,
} from './live/sections'
import { MAX_RANGE_DAYS, addDays, daysBetween, toIso } from './live/format'
import { MsgId, useFormat, useT } from './live/i18n'
import { KpiCards } from './live/KpiCards'
import { DailyConsumptionCard } from './live/DailyConsumptionCard'
import { OperationalCard, SeasonalityCard, YearlyCard } from './live/DailyCards'
import { HeatmapCard, ProfilesCard } from './live/HourlyCards'
import { EventsCard } from './live/EventsCard'
import { WeekdayCard } from './live/WeekdayCard'

type Option = { value: number; label: string }

const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 42,
    borderColor: '#E4E6EF',
    borderRadius: 8,
    boxShadow: 'none',
    '&:hover': { borderColor: '#B5B5C3' },
  }),
  menu: (base: Record<string, unknown>) => ({ ...base, borderRadius: 8, zIndex: 30 }),
}

/** Last 12 months ending on `end`. */
const yearEndingOn = (end: string) => ({ start: addDays(end, -364), end })

/** The date inputs are free text while typing: only a complete, ordered, not-too-long range is used. */
function rangeError(start: string, end: string): MsgId | null {
  if (!start || !end) return 'PROFILES_DASHBOARD.RANGE.ERROR_MISSING'
  if (start > end) return 'PROFILES_DASHBOARD.RANGE.ERROR_ORDER'
  if (daysBetween(start, end) > MAX_RANGE_DAYS) return 'PROFILES_DASHBOARD.RANGE.ERROR_LONG'
  return null
}

const ProfilesDashboard = () => {
  const t = useT()
  const f = useFormat()
  const meterLabel = (type: MeterType) =>
    t(type === 'Gas' ? 'PROFILES_DASHBOARD.METER.GAS' : 'PROFILES_DASHBOARD.METER.ELECTRICITY')
  // lower-case noun used inside a sentence ("1 electricity meter", "1 comptador d'electricitat")
  const meterNoun = (type: MeterType) =>
    t(
      type === 'Gas'
        ? 'PROFILES_DASHBOARD.METER_LOWER.GAS'
        : 'PROFILES_DASHBOARD.METER_LOWER.ELECTRICITY'
    )
  const today = toIso(new Date())
  const filters = useFilters()
  const [companyId, setCompanyId] = useState<number | null>(null)
  const [siteId, setSiteId] = useState<number | null>(null)
  const [meterType, setMeterType] = useState<MeterType>('Electricity')
  const [range, setRange] = useState(() => yearEndingOn(today))
  const [sections, setSections] = useState<SectionConfig[]>(loadSections)
  const [customizeOpen, setCustomizeOpen] = useState(false)

  useEffect(() => saveSections(sections), [sections])

  const companies = useMemo(
    () => (filters.status === 'ready' ? filters.data.companies : []),
    [filters]
  )
  const allSites = useMemo(() => (filters.status === 'ready' ? filters.data.sites : []), [filters])
  const companySites = useMemo(
    () => allSites.filter((s) => s.company_id === companyId),
    [allSites, companyId]
  )
  const site: FilterSite | undefined = companySites.find((s) => s.id === siteId)

  // first load: pick the first company, its first site and a meter type that site has
  const selectSite = useCallback((next: FilterSite | undefined) => {
    setSiteId(next ? next.id : null)
    if (next) {
      setMeterType((current) =>
        next.meter_types.includes(current) ? current : next.meter_types[0]
      )
    }
  }, [])
  useEffect(() => {
    if (filters.status === 'ready' && companyId === null && companies.length) {
      const first = companies[0].id
      setCompanyId(first)
      selectSite(allSites.find((s) => s.company_id === first))
    }
  }, [filters.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const error = rangeError(range.start, range.end)
  const params: DashboardParams | null =
    site && !error ? { site: site.id, meterType, start: range.start, end: range.end } : null

  const visible = (id: SectionId) => sections.find((s) => s.id === id)?.visible !== false
  const chartType = (id: SectionId) => sections.find((s) => s.id === id)?.chartType || 'line'

  // hidden sections cost nothing: their data is not requested
  const { daily, events, hourly } = useDashboardData(params, {
    daily: ['kpis', 'timeseries', 'seasonality', 'weekdays', 'operational', 'yearly'].some((id) =>
      visible(id as SectionId)
    ),
    events: visible('events'),
    hourly: ['kpis', 'heatmap', 'profiles'].some((id) => visible(id as SectionId)),
  })

  const updateSection = (id: SectionId, patch: Partial<SectionConfig>) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))

  const companyOptions: Option[] = companies.map((c) => ({ value: c.id, label: c.name }))
  const siteOptions: Option[] = companySites.map((s) => ({ value: s.id, label: s.name }))

  const availableTo = daily.status === 'ready' ? daily.data.meta.available_to : null
  const noDataInPeriod = daily.status === 'ready' && daily.data.series.length === 0 && availableTo

  if (filters.status === 'error') {
    return (
      <div className='alert alert-danger' role='alert'>
        {filters.message}
      </div>
    )
  }
  if (filters.status === 'ready' && !companies.length) {
    return <div className='card card-body text-muted'>{t('PROFILES_DASHBOARD.NO_SITES')}</div>
  }

  return (
    <>
      <div className='card border-0 shadow-sm mb-5'>
        <div className='card-body py-4 px-5'>
          <div className='row g-4 align-items-end'>
            <div className='col-12 col-md-3'>
              <label htmlFor='dashboard-company' className='form-label fs-7 text-gray-600 mb-1'>
                {t('PROFILES_DASHBOARD.FILTER.COMPANY')}
              </label>
              <Select<Option, false>
                inputId='dashboard-company'
                styles={selectStyles}
                classNamePrefix='react-select'
                isLoading={filters.status === 'loading'}
                value={companyOptions.find((o) => o.value === companyId) || null}
                onChange={(o: SingleValue<Option>) => {
                  if (!o) return
                  setCompanyId(o.value)
                  selectSite(allSites.find((s) => s.company_id === o.value))
                }}
                options={companyOptions}
              />
            </div>
            <div className='col-12 col-md-3'>
              <label htmlFor='dashboard-site' className='form-label fs-7 text-gray-600 mb-1'>
                {t('PROFILES_DASHBOARD.FILTER.SITE')}
              </label>
              <Select<Option, false>
                inputId='dashboard-site'
                styles={selectStyles}
                classNamePrefix='react-select'
                isLoading={filters.status === 'loading'}
                value={siteOptions.find((o) => o.value === siteId) || null}
                onChange={(o: SingleValue<Option>) =>
                  o && selectSite(companySites.find((s) => s.id === o.value))
                }
                options={siteOptions}
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label fs-7 text-gray-600 mb-1'>
                {t('PROFILES_DASHBOARD.FILTER.METER_TYPE')}
              </label>
              <div
                className='btn-group w-100'
                role='group'
                aria-label={t('PROFILES_DASHBOARD.FILTER.METER_TYPE')}
              >
                {(['Electricity', 'Gas'] as MeterType[]).map((type) => {
                  const available = !!site && site.meter_types.includes(type)
                  return (
                    <button
                      key={type}
                      type='button'
                      className={`btn btn-sm ${meterType === type ? 'btn-primary' : 'btn-light'}`}
                      disabled={!available}
                      onClick={() => setMeterType(type)}
                    >
                      {meterLabel(type)}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className='col-6 col-md-2'>
              <label htmlFor='dashboard-start' className='form-label fs-7 text-gray-600 mb-1'>
                {t('PROFILES_DASHBOARD.FILTER.START')}
              </label>
              <input
                id='dashboard-start'
                type='date'
                className='form-control form-control-solid'
                value={range.start}
                max={range.end || today}
                onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              />
            </div>
            <div className='col-6 col-md-2'>
              <label htmlFor='dashboard-end' className='form-label fs-7 text-gray-600 mb-1'>
                {t('PROFILES_DASHBOARD.FILTER.END')}
              </label>
              <input
                id='dashboard-end'
                type='date'
                className='form-control form-control-solid'
                value={range.end}
                min={range.start}
                max={today}
                onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              />
            </div>
          </div>
          <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mt-4'>
            <div className='d-flex flex-wrap gap-2'>
              {daily.status === 'ready' ? (
                <>
                  <span className='badge badge-light-primary'>
                    {t('PROFILES_DASHBOARD.BADGE.DAYS', {
                      count: daily.data.meta.days_with_data,
                      start: f.date(range.start),
                      end: f.date(range.end),
                    })}
                  </span>
                  <span className='badge badge-light'>
                    {t('PROFILES_DASHBOARD.BADGE.METERS', {
                      count: daily.data.meta.meter_count,
                      type: meterNoun(meterType),
                    })}
                  </span>
                  {daily.data.meta.available_from ? (
                    <span className='badge badge-light'>
                      {t('PROFILES_DASHBOARD.BADGE.AVAILABLE', {
                        from: f.date(daily.data.meta.available_from),
                        to: f.date(daily.data.meta.available_to),
                      })}
                    </span>
                  ) : null}
                </>
              ) : null}
            </div>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setCustomizeOpen(true)}
            >
              {t('PROFILES_DASHBOARD.CUSTOMIZE.BUTTON')}
            </button>
          </div>
          {error ? <div className='text-danger fs-7 mt-3'>{t(error)}</div> : null}
          {noDataInPeriod ? (
            <div className='mt-3 fs-7'>
              <span className='text-muted me-3'>{t('PROFILES_DASHBOARD.RANGE.NO_READINGS')}</span>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => setRange(yearEndingOn(availableTo as string))}
              >
                {t('PROFILES_DASHBOARD.RANGE.SHOW_LATEST')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {visible('kpis') && <KpiCards daily={daily} hourly={hourly} />}

      {visible('timeseries') && (
        <div className='mb-5' style={{ minWidth: 0 }}>
          <DailyConsumptionCard daily={daily} chartType={chartType('timeseries')} />
        </div>
      )}

      <div className='row g-4 mb-5'>
        {visible('seasonality') && (
          <div className='col-lg-6' style={{ minWidth: 0 }}>
            <SeasonalityCard daily={daily} chartType={chartType('seasonality')} />
          </div>
        )}
        {visible('weekdays') && (
          <div className='col-lg-6' style={{ minWidth: 0 }}>
            <WeekdayCard daily={daily} chartType={chartType('weekdays')} />
          </div>
        )}
      </div>

      <div className='row g-4 mb-5'>
        {visible('heatmap') && (
          <div className='col-lg-6' style={{ minWidth: 0 }}>
            <HeatmapCard hourly={hourly} />
          </div>
        )}
        {visible('events') && (
          <div className='col-lg-6' style={{ minWidth: 0 }}>
            <EventsCard events={events} chartType={chartType('events')} />
          </div>
        )}
      </div>

      <div className='row g-4 mb-5'>
        {visible('operational') && (
          <div className='col-lg-6' style={{ minWidth: 0 }}>
            <OperationalCard daily={daily} chartType={chartType('operational')} />
          </div>
        )}
        {visible('yearly') && (
          <div className='col-lg-6' style={{ minWidth: 0 }}>
            <YearlyCard daily={daily} chartType={chartType('yearly')} />
          </div>
        )}
      </div>

      {visible('profiles') && (
        <div className='mb-5' style={{ minWidth: 0 }}>
          <ProfilesCard hourly={hourly} chartType={chartType('profiles')} />
        </div>
      )}

      <Modal show={customizeOpen} onHide={() => setCustomizeOpen(false)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('PROFILES_DASHBOARD.CUSTOMIZE.TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted fs-7 mb-4'>{t('PROFILES_DASHBOARD.CUSTOMIZE.HELP')}</p>
          <div className='table-responsive'>
            <table className='table align-middle mb-0'>
              <thead>
                <tr className='text-muted fs-7 text-uppercase'>
                  <th>{t('PROFILES_DASHBOARD.CUSTOMIZE.COL_SECTION')}</th>
                  <th>{t('PROFILES_DASHBOARD.CUSTOMIZE.COL_VISIBLE')}</th>
                  <th>{t('PROFILES_DASHBOARD.CUSTOMIZE.COL_CHART')}</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.id}>
                    <td className='fw-semibold'>{t(SECTION_LABEL[s.id])}</td>
                    <td>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          aria-label={t('PROFILES_DASHBOARD.CUSTOMIZE.ARIA_SHOW', {
                            section: t(SECTION_LABEL[s.id]),
                          })}
                          checked={s.visible}
                          onChange={(e) => updateSection(s.id, { visible: e.target.checked })}
                        />
                      </div>
                    </td>
                    <td style={{ minWidth: 160 }}>
                      {!s.configurable ? (
                        <span className='text-muted fs-7'>
                          {t('PROFILES_DASHBOARD.CUSTOMIZE.FIXED')}
                        </span>
                      ) : (
                        <select
                          className='form-select form-select-sm'
                          aria-label={t('PROFILES_DASHBOARD.CUSTOMIZE.ARIA_CHART', {
                            section: t(SECTION_LABEL[s.id]),
                          })}
                          value={s.chartType}
                          onChange={(e) =>
                            updateSection(s.id, {
                              chartType: e.target.value as SectionConfig['chartType'],
                            })
                          }
                        >
                          <option value='line'>
                            {t('PROFILES_DASHBOARD.CUSTOMIZE.CHART_LINE')}
                          </option>
                          <option value='area'>
                            {t('PROFILES_DASHBOARD.CUSTOMIZE.CHART_AREA')}
                          </option>
                          <option value='bar'>{t('PROFILES_DASHBOARD.CUSTOMIZE.CHART_BAR')}</option>
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => setSections(DEFAULT_SECTIONS.map((s) => ({ ...s })))}
          >
            {t('PROFILES_DASHBOARD.CUSTOMIZE.RESET')}
          </button>
          <button type='button' className='btn btn-primary' onClick={() => setCustomizeOpen(false)}>
            {t('PROFILES_DASHBOARD.CUSTOMIZE.DONE')}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ProfilesDashboard
